// src/components/AuthSwitch.tsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  listenForAuthChanges,
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
} from "../redux/authSlice";
import { useNavigate } from "react-router-dom";

/* ─── global font injection ─────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

const AuthSwitch: React.FC = () => {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, user, authLoaded } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (authLoaded && user) {
      navigate("/");
    }
  }, [authLoaded, user, navigate]);

  useEffect(() => {
    dispatch(listenForAuthChanges());
  }, [dispatch]);

  const handleToggle = (newMode: "login" | "signup") => {
    setMode(newMode);
    setError("");
    setPassword("");
    setConfirmPassword("");
  };

  // ---------- Password Validation ----------
  const extractUsername = (mail: string) =>
    mail.split("@")[0]?.toLowerCase() || "";

  const validatePassword = (pwd: string, email: string) => {
    const username = extractUsername(email);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
    const validLength = pwd.length >= 6 && pwd.length <= 12;
    const notEmail = pwd.toLowerCase() !== email.toLowerCase();
    const notContainEmail =
      !pwd.toLowerCase().includes(email.toLowerCase()) &&
      !pwd.toLowerCase().includes(username);

    return {
      hasUpper,
      hasLower,
      hasNumber,
      hasSpecial,
      validLength,
      notEmail,
      notContainEmail,
    };
  };

  const check = validatePassword(password, email);
  const allValid =
    check.hasUpper &&
    check.hasLower &&
    check.hasNumber &&
    check.hasSpecial &&
    check.validLength &&
    check.notEmail &&
    check.notContainEmail;

  const strengthScore = [
    check.hasUpper,
    check.hasLower,
    check.hasNumber,
    check.hasSpecial,
    check.validLength,
  ].filter(Boolean).length;

  const strengthLabel =
    ["Too Weak", "Weak", "Good", "Strong", "Very Strong"][strengthScore] ||
    "Too Weak";

  const handleLogin = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    try {
      await dispatch(loginWithEmail({ email, password })).unwrap();
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Login failed");
    }
  };

  const handleRegister = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    if (password !== confirmPassword)
      return setError("Passwords do not match");
    if (!allValid) return setError("Password must meet all requirements.");

    try {
      const [firstName = "", lastName = ""] = email.split("@")[0].split(".");
      await dispatch(
        registerWithEmail({
          email,
          password,
          firstName,
          lastName,
          phoneNumber: "",
          profilePicture: "",
          username: ""
        })
      ).unwrap();
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    }
  };

  const handleGoogle = async () => {
    try {
      await dispatch(loginWithGoogle()).unwrap();
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Google login failed");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#000000",
        p: 2,
        fontFamily: "'Montserrat', 'Cormorant Garamond', sans-serif",
      }}
    >
      <style>{FONT_CSS}</style>

      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          background: "#000000",
          border: "1px solid #1c1c1c",
          borderRadius: 0,
          p: 4,
        }}
      >
        {/* Brand */}
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300,
            fontSize: 28,
            letterSpacing: "0.3em",
            color: "#ffffff",
            mb: 4,
          }}
        >
          COSMOOLA
        </Typography>

        {/* Toggle Switch */}
        <Box display="flex" justifyContent="center" mb={4}>
          <div
            style={{
              width: 280,
              height: 52,
              display: "flex",
              alignItems: "center",
              position: "relative",
              cursor: "pointer",
              border: "1px solid #2a2a2a",
              overflow: "hidden",
            }}
          >
            <motion.div
              key={mode}
              initial={{ x: mode === "login" ? "-100%" : "100%" }}
              animate={{ x: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              style={{
                position: "absolute",
                top: 0,
                left: mode === "login" ? 0 : "50%",
                width: "50%",
                height: "100%",
                background: "#ffffff",
              }}
            />
            <div
              onClick={() => handleToggle("login")}
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: 500,
                fontSize: 12,
                letterSpacing: "0.2em",
                color: mode === "login" ? "#000000" : "#6b6b6b",
                zIndex: 2,
                userSelect: "none",
                fontFamily: "'Montserrat', sans-serif",
                transition: "color 0.3s",
              }}
            >
              LOGIN
            </div>
            <div
              onClick={() => handleToggle("signup")}
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: 500,
                fontSize: 12,
                letterSpacing: "0.2em",
                color: mode === "signup" ? "#000000" : "#6b6b6b",
                zIndex: 2,
                userSelect: "none",
                fontFamily: "'Montserrat', sans-serif",
                transition: "color 0.3s",
              }}
            >
              SIGNUP
            </div>
          </div>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 400,
            fontSize: 22,
            textAlign: "center",
            mb: 3,
            color: "#e8e8e8",
            letterSpacing: "0.04em",
          }}
        >
          {mode === "login" ? "Sign in to Cosmoola" : "Create your account"}
        </Typography>

        {/* Form Fields */}
        <TextField
          label="Email"
          fullWidth
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 13,
              color: "#e8e8e8",
              "& fieldset": { borderColor: "#2a2a2a" },
              "&:hover fieldset": { borderColor: "#555555" },
              "&.Mui-focused fieldset": { borderColor: "#ffffff" },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 12,
              letterSpacing: "0.05em",
              color: "#6b6b6b",
              "&.Mui-focused": { color: "#ffffff" },
            },
          }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={mode === "signup" && password.length > 0 && !allValid}
          helperText={
            mode === "signup" && password ? `Strength: ${strengthLabel}` : ""
          }
          sx={{
            mb: mode === "signup" ? 1 : 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 13,
              color: "#e8e8e8",
              "& fieldset": { borderColor: "#2a2a2a" },
              "&:hover fieldset": { borderColor: "#555555" },
              "&.Mui-focused fieldset": { borderColor: "#ffffff" },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 12,
              letterSpacing: "0.05em",
              color: "#6b6b6b",
              "&.Mui-focused": { color: "#ffffff" },
            },
            "& .MuiFormHelperText-root": {
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 11,
              letterSpacing: "0.05em",
              color: "#6b6b6b",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{ color: "#6b6b6b" }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Password checklist — signup only */}
        {mode === "signup" && password && (
          <Box
            sx={{
              mb: 2,
              fontSize: 11,
              color: "#6b6b6b",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.03em",
            }}
          >
            <div style={{ marginBottom: 6, letterSpacing: "0.12em", fontSize: 10, color: "#555" }}>
              REQUIREMENTS
            </div>
            <ul style={{ margin: 0, padding: "0 0 0 16px", listStyle: "none" }}>
              {[
                { ok: check.hasUpper, text: "At least 1 uppercase letter" },
                { ok: check.hasLower, text: "At least 1 lowercase letter" },
                { ok: check.hasNumber, text: "At least 1 number" },
                { ok: check.hasSpecial, text: "At least 1 special character" },
                { ok: check.validLength, text: "6–12 characters long" },
                { ok: check.notEmail, text: "Not your email" },
                { ok: check.notContainEmail, text: "Doesn't contain email/username" },
              ].map((item, i) => (
                <li
                  key={i}
                  style={{
                    color: item.ok ? "#ffffff" : "#555555",
                    padding: "3px 0",
                    fontSize: 11,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{
                    width: 6, height: 6,
                    borderRadius: 0,
                    background: item.ok ? "#ffffff" : "#333333",
                    border: `1px solid ${item.ok ? "#ffffff" : "#555555"}`,
                    display: "inline-block",
                    flexShrink: 0,
                  }} />
                  {item.text}
                </li>
              ))}
            </ul>
          </Box>
        )}

        {mode === "signup" && (
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword.length > 0 && confirmPassword !== password}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 13,
                color: "#e8e8e8",
                "& fieldset": { borderColor: "#2a2a2a" },
                "&:hover fieldset": { borderColor: "#555555" },
                "&.Mui-focused fieldset": { borderColor: "#ffffff" },
              },
              "& .MuiInputLabel-root": {
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 12,
                letterSpacing: "0.05em",
                color: "#6b6b6b",
                "&.Mui-focused": { color: "#ffffff" },
              },
            }}
          />
        )}

        {error && (
          <Typography
            sx={{
              fontSize: 12,
              textAlign: "center",
              mb: 2,
              color: "#888888",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.03em",
            }}
          >
            {error}
          </Typography>
        )}

        {/* Main CTA */}
        <Button
          fullWidth
          onClick={mode === "login" ? handleLogin : handleRegister}
          disabled={status === "loading"}
          sx={{
            py: 1.6,
            fontWeight: 500,
            borderRadius: 0,
            background: "#ffffff",
            color: "#000000",
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 12,
            letterSpacing: "0.2em",
            "&:hover": {
              background: "#e8e8e8",
            },
            "&:disabled": {
              background: "#333333",
              color: "#6b6b6b",
            },
            mb: 2,
          }}
        >
          {status === "loading"
            ? mode === "login"
              ? "SIGNING IN..."
              : "REGISTERING..."
            : mode === "login"
            ? "LOGIN"
            : "SIGNUP"}
        </Button>

        {/* Divider */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, height: "1px", background: "#1c1c1c" }} />
          <Typography
            sx={{
              fontSize: 10,
              color: "#555555",
              letterSpacing: "0.15em",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            OR
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: "#1c1c1c" }} />
        </Box>

        {/* Google button */}
        <Button
          fullWidth
          onClick={handleGoogle}
          variant="outlined"
          startIcon={<Google sx={{ fontSize: 16 }} />}
          sx={{
            py: 1.5,
            fontWeight: 500,
            borderRadius: 0,
            borderColor: "#2a2a2a",
            color: "#6b6b6b",
            fontFamily: "'Montserrat', sans-serif",
            fontSize: 11,
            letterSpacing: "0.1em",
            "&:hover": {
              background: "rgba(255,255,255,0.04)",
              borderColor: "#ffffff",
              color: "#ffffff",
            },
          }}
        >
          Continue with Google
        </Button>

        {/* Footer Links */}
        {mode === "login" && (
          <Typography
            sx={{
              textAlign: "center",
              fontSize: 12,
              mt: 3,
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.03em",
            }}
          >
            <span
              style={{
                cursor: "pointer",
                color: "#6b6b6b",
                borderBottom: "1px solid #2a2a2a",
                paddingBottom: 2,
                transition: "color 0.2s, border-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#ffffff";
                e.currentTarget.style.borderColor = "#ffffff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#6b6b6b";
                e.currentTarget.style.borderColor = "#2a2a2a";
              }}
              onClick={() => navigate("/ResetPassword")}
            >
              Forgot password?
            </span>
          </Typography>
        )}
        {mode === "signup" && (
          <Typography
            sx={{
              textAlign: "center",
              fontSize: 12,
              mt: 3,
              color: "#6b6b6b",
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: "0.03em",
            }}
          >
            Have an account?{" "}
            <span
              style={{
                cursor: "pointer",
                color: "#ffffff",
                borderBottom: "1px solid #2a2a2a",
                paddingBottom: 2,
              }}
              onClick={() => handleToggle("login")}
            >
              Login
            </span>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AuthSwitch;